/**
 * Date-only values from the API ("2026-08-31", "2026-08") are calendar dates,
 * not instants: they are formatted in UTC so a negative local offset cannot
 * shift them back a day. Timestamps keep the viewer's own time zone.
 */
const CALENDAR_DATE = { timeZone: "UTC" } as const;

const dayFormatter = new Intl.DateTimeFormat("en-US", {
  ...CALENDAR_DATE,
  month: "short",
  day: "numeric",
  year: "numeric",
});

/** No year: the shipment table's cells clamp to one line. */
const shortDayFormatter = new Intl.DateTimeFormat("en-US", {
  ...CALENDAR_DATE,
  month: "short",
  day: "numeric",
});

const monthFormatter = new Intl.DateTimeFormat("en-US", {
  ...CALENDAR_DATE,
  month: "long",
  year: "numeric",
});

const timestampFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

const parse = (value: string | null | undefined): Date | null => {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
};

/** "Aug 31, 2026" from a calendar date such as "2026-08-31". */
export const formatDate = (value: string | null | undefined): string => {
  const date = parse(value);

  return date === null ? "" : dayFormatter.format(date);
};

/**
 * "Aug 31", or "Aug 31, 2025" once the year stops being the current one —
 * a date in this year does not need saying twice on every row.
 */
export const formatShortDate = (value: string | null | undefined): string => {
  const date = parse(value);

  if (date === null) return "";

  return date.getUTCFullYear() === new Date().getUTCFullYear()
    ? shortDayFormatter.format(date)
    : dayFormatter.format(date);
};

/** "July 2026" from a period such as "2026-07". */
export const formatMonth = (period: string | null | undefined): string => {
  const date = parse(period ? `${period}-01` : null);

  return date === null ? "" : monthFormatter.format(date);
};

/** "Aug 31, 2026" from a full timestamp, in the viewer's time zone. */
export const formatTimestamp = (value: string | null | undefined): string => {
  const date = parse(value);

  return date === null ? "" : timestampFormatter.format(date);
};

/**
 * How long ago, in the compact form the activity feed uses: "12 min", "2 h",
 * "Yesterday", then the date once "days ago" stops being easier to read than
 * the day itself.
 */
export const formatRelativeTime = (value: string | null | undefined): string => {
  const date = parse(value);

  if (date === null) return "";

  const minutes = Math.floor((Date.now() - date.getTime()) / 60_000);

  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} min`;

  const hours = Math.floor(minutes / 60);

  if (hours < 24) return `${hours} h`;

  const days = Math.floor(hours / 24);

  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days`;

  return formatShortDate(value);
};
